/* eslint-disable no-underscore-dangle */
import {
  COVERAGE_TIERS,
  MP_NETWORK_TYPE_IDS,
  NO_DEDUCTIBLE_PLAN_CARRIERS,
  OON_NETWORK_TYPE_IDS,
} from 'Containers/App/constants';
import {
  DrugCoverage,
  HealthCoverage,
  NetworkTier,
  Recommendation,
  RxCoverageForTier,
  RxTierGroup,
  Thresholds,
  Tier,
} from 'Types/entities';
import { calculateHsaRecommendation } from 'Utils/hsa';

class Plan {
  _recommendation: Recommendation;

  _planId: string;

  _planType: string;

  _picwellScore: number;

  _tier: Tier;

  _carrierName: string;

  _planMarketingName: string;

  _hsaEligible: boolean;

  _hraEligible: boolean;

  _premium: number;

  _annualPremium: number;

  _annualPremiumDeductions: number;

  _spendingAccountBenefit: number;

  _medicalCost: number;

  _drugCost: number;

  _employerHraContribution: number | null;

  _employerHsaFixedContribution: number | null;

  _employerHsaPercentageMatch: number | null;

  _employerHsaContributionLimit: number | null;

  _annualSihraDollarsUsed?: number;

  _individualThresholds: Thresholds;

  _individualChildThresholds: Thresholds;

  _individualChildrenThresholds: Thresholds;

  _individualSpouseThresholds: Thresholds;

  _familyThresholds: Thresholds;

  _primaryCare: HealthCoverage;

  _specialistCare: HealthCoverage;

  _realCostBeforeTaxBenefit: number;

  _benefitsSummaryUrl: string | null;

  _nonFormularyDrug: DrugCoverage;

  _inNetworkDeductible: number | null;

  _inNetworkMedDeductible: number | null;

  _inNetworkDrugDeductible: number | null;

  _oonDeductible: number | null;

  _oonMedDeductible: number | null;

  _oonDrugDeductible: number | null;

  _inNetworkOopMax: number | null;

  _inNetworkMedOopMax: number | null;

  _inNetworkDrugOopMax: number | null;

  _oonOopMax: number | null;

  _oonMedOopMax: number | null;

  _oonDrugOopMax: number | null;

  _secondaryInNetworkDeductible: number | null;

  _secondaryInNetworkMedDeductible: number | null;

  _secondaryInNetworkDrugDeductible: number | null;

  _secondaryInNetworkOopMax: number | null;

  _secondaryInNetworkMedOopMax: number | null;

  _secondaryInNetworkDrugOopMax: number | null;

  _combinedNetworkOopMax: number | null;

  _combinedNetworkMedOopMax: number | null;

  _combinedNetworkDrugOopMax: number | null;

  _combinedNetworkDeductible: number | null;

  _combinedNetworkMedDeductible: number | null;

  _combinedNetworkDrugDeductible: number | null;

  _canReimburseCopay: boolean | null;

  _docfinderNetworkUuids: string[] | null;

  _rxTierGroup: RxTierGroup | null;

  _hraHasLimitedProviderReimbursement: boolean | null;

  _hraIsPostDeductibleWithHSA: boolean | null;

  _annualHraEmployerDollarsUsed: number;

  _annualHsaEmployerDollarsUsed: number;

  _useCustomTierName: boolean | null = null;

  _customTierName: string | null = null;

  _primaryCareTiers: Recommendation['plan']['primary_care_tiers'] = null;

  _specialistCareTiers: Recommendation['plan']['specialist_care_tiers'] = null;

  _networkTypeId: string | null = null;

  _rxTierOutOfNetwork: RxTierGroup | null = null;

  _hasRxNetwork: boolean | null = null;

  _rxHasOutOfNetwork: boolean | null = null;

  _showRxOonCoverage: boolean | null = null;

  constructor(recommendation: Recommendation) {
    this._recommendation = recommendation;
    this._planId = recommendation.plan.external_id || recommendation.plan.external_plan_id;
    this._planType = recommendation.plan.plan_type;
    this._picwellScore = recommendation.score;
    this._tier = recommendation.tier;
    this._carrierName = recommendation.plan.carrier_name;
    this._planMarketingName = recommendation.plan.plan_marketing_name;
    this._hsaEligible = recommendation.plan.hsa_eligible;
    this._hraEligible = recommendation.plan.hra_eligible;
    this._premium = recommendation.costs.effective_premium;
    this._annualPremium = recommendation.costs.annual_premium;
    this._annualPremiumDeductions = recommendation.costs.annual_premium_deductions;
    this._spendingAccountBenefit = recommendation.costs.spending_account_benefit;
    this._medicalCost = recommendation.costs.services.in_network;
    this._drugCost = recommendation.costs.drugs;
    this._employerHraContribution =
      recommendation.plan.spending_account_contributions.hra_annual?.employer_contribution_amount ?? 0;
    this._employerHsaFixedContribution =
      recommendation.plan.spending_account_contributions.hsa_annual.employer_contribution_amount;
    this._employerHsaPercentageMatch =
      recommendation.plan.spending_account_contributions.hsa_annual.employer_contribution_percent;
    this._employerHsaContributionLimit =
      recommendation.plan.spending_account_contributions.hsa_annual.employer_contribution_limit;
    this._annualSihraDollarsUsed = recommendation.costs.annual_sihra_dollars_used;
    this._realCostBeforeTaxBenefit = recommendation.costs.real_cost_before_tax_benefit;

    const moop = recommendation.plan_details.max_oop;
    const ded = recommendation.plan_details.deductible;

    this._inNetworkDeductible = ded.comprehensive?.in_network?.total || null;
    this._inNetworkMedDeductible = ded.health_and_drug?.health?.in_network?.total || null;
    this._inNetworkDrugDeductible = ded.health_and_drug?.drug?.in_network?.total || null;

    this._oonDeductible = ded.comprehensive?.out_of_network?.total || null;
    this._oonMedDeductible = ded.health_and_drug?.health?.out_of_network?.total || null;
    this._oonDrugDeductible = ded.health_and_drug?.drug?.out_of_network?.total || null;

    this._combinedNetworkDeductible = ded.comprehensive?.combined?.total || null;
    this._combinedNetworkMedDeductible = ded.health_and_drug?.health?.combined?.total || null;
    this._combinedNetworkDrugDeductible = ded.health_and_drug?.drug?.combined?.total || null;

    this._inNetworkOopMax = moop.comprehensive?.in_network?.total || null;
    this._inNetworkMedOopMax = moop.health_and_drug?.health?.in_network?.total || null;
    this._inNetworkDrugOopMax = moop.health_and_drug?.drug?.in_network?.total || null;

    this._oonOopMax = moop.comprehensive?.out_of_network?.total || null;
    this._oonMedOopMax = moop.health_and_drug?.health?.out_of_network?.total || null;
    this._oonDrugOopMax = moop.health_and_drug?.drug?.out_of_network?.total || null;

    this._secondaryInNetworkDeductible = ded.comprehensive?.secondary_in_network?.total || null;
    this._secondaryInNetworkMedDeductible = ded.health_and_drug?.health?.secondary_in_network?.total || null;
    this._secondaryInNetworkDrugDeductible = ded.health_and_drug?.drug?.secondary_in_network?.total || null;
    this._secondaryInNetworkOopMax = moop.comprehensive?.secondary_in_network?.total || null;
    this._secondaryInNetworkMedOopMax = moop.health_and_drug?.health?.secondary_in_network?.total || null;
    this._secondaryInNetworkDrugOopMax = moop.health_and_drug?.drug?.secondary_in_network?.total || null;

    this._combinedNetworkOopMax = moop.comprehensive?.combined?.total || null;
    this._combinedNetworkMedOopMax = moop.health_and_drug?.health?.combined?.total || null;
    this._combinedNetworkDrugOopMax = moop.health_and_drug?.drug?.combined?.total || null;

    this._individualThresholds = recommendation.plan.individual_thresholds;
    this._individualChildThresholds = recommendation.plan.individual_child_thresholds;
    this._individualChildrenThresholds = recommendation.plan.individual_children_thresholds;
    this._individualSpouseThresholds = recommendation.plan.individual_spouse_thresholds;
    this._familyThresholds = recommendation.plan.family_thresholds;
    this._primaryCare = recommendation.plan.primary_care;
    this._specialistCare = recommendation.plan.specialist_care;
    this._benefitsSummaryUrl = recommendation.plan.benefits_summary_url;
    this._nonFormularyDrug = recommendation.plan.non_formulary_drug;
    this._canReimburseCopay =
      recommendation.plan_details.health_reimbursement_arrangement?.can_reimburse_copay || null;
    this._docfinderNetworkUuids = recommendation.plan.docfinder_network_uuids;
    this._rxTierGroup = recommendation.plan.rx_tier_group || null;

    this._hraHasLimitedProviderReimbursement =
      recommendation.plan_details.health_reimbursement_arrangement?.limited_provider_reimbursement || null;
    this._hraIsPostDeductibleWithHSA =
      recommendation.plan_details.health_reimbursement_arrangement?.hra_post_deductible_with_hsa || null;

    this._annualHraEmployerDollarsUsed = recommendation.costs.annual_hra_employer_dollars_used ?? 0;
    this._annualHsaEmployerDollarsUsed = recommendation.costs.annual_hsa_employer_dollars_used ?? 0;
    this._useCustomTierName = recommendation.plan.use_custom_tier_name ?? null;
    this._customTierName = recommendation.plan.custom_tier_name ?? null;
    this._primaryCareTiers = recommendation.plan.primary_care_tiers ?? null;
    this._specialistCareTiers = recommendation.plan.specialist_care_tiers ?? null;
    this._networkTypeId = recommendation.plan.network_type_id ?? null;
    this._rxTierOutOfNetwork = recommendation.plan.rx_tier_out_of_network ?? null;
    this._hasRxNetwork = recommendation.plan.has_rx_network ?? null;
    this._rxHasOutOfNetwork = recommendation.plan.rx_has_out_of_network ?? null;
    this._showRxOonCoverage = recommendation.plan.show_rx_oon_coverage ?? null;
  }

  /* Getters & Setters */

  get useCustomTierName() {
    return this._useCustomTierName;
  }

  get customTierName() {
    return this._customTierName;
  }

  get primaryCareTiers() {
    return this._primaryCareTiers;
  }

  get specialistCareTiers() {
    return this._specialistCareTiers;
  }

  get networkTypeId() {
    return this._networkTypeId;
  }

  hasOonCoverage() {
    return this._networkTypeId != null && OON_NETWORK_TYPE_IDS.has(this._networkTypeId);
  }

  get rxTierGroup() {
    return this._rxTierGroup;
  }

  get rxTierOutOfNetwork() {
    return this._rxTierOutOfNetwork;
  }

  get hasRxNetwork() {
    return this._hasRxNetwork;
  }

  get rxHasOutOfNetwork() {
    return this._rxHasOutOfNetwork;
  }

  get showRxOonCoverage() {
    return this._showRxOonCoverage;
  }

  /**
   * Resolves what Rx coverage information to display for the selected network tier.
   *
   * Builder does not allow configuring Rx benefits for a most-preferred network, so
   * 'most_preferred' shows the same in-network Rx info as 'in_network'. For 'out_of_network':
   *
   *   - Rx has no network associated with it → Rx does not vary by network, show in-network info
   *   - Rx has no out-of-network coverage    → 'inNetworkOnly'
   *   - OON costs are not published          → 'seePlanDocuments'
   *   - otherwise                            → the out-of-network tier costs
   */
  getRxCoverageForTier(tier: NetworkTier): RxCoverageForTier {
    if (tier !== 'out_of_network' || !this._hasRxNetwork) {
      return { display: 'tiers', tierGroup: this._rxTierGroup, isOutOfNetwork: false };
    }

    if (!this._rxHasOutOfNetwork) {
      return { display: 'inNetworkOnly' };
    }

    if (!this._showRxOonCoverage) {
      return { display: 'seePlanDocuments' };
    }

    return { display: 'tiers', tierGroup: this._rxTierOutOfNetwork, isOutOfNetwork: true };
  }

  get recommendation() {
    return this._recommendation;
  }

  get planId() {
    return this._planId;
  }

  get picwellScore() {
    return this._picwellScore;
  }

  get planType() {
    return this._planType;
  }

  set planType(newPlanType) {
    this._planType = newPlanType;
  }

  get tier() {
    return this._tier;
  }

  get carrierName() {
    return this._carrierName;
  }

  set carrierName(newCarrierName) {
    this._carrierName = newCarrierName;
  }

  get planMarketingName() {
    return this._planMarketingName;
  }

  set planMarketingName(newPlanMarketingName) {
    this._planMarketingName = newPlanMarketingName;
  }

  get hsaEligible() {
    return this._hsaEligible;
  }

  get hraEligible() {
    return this._hraEligible;
  }

  get premium() {
    return this._premium;
  }

  set premium(newPremium) {
    this._premium = newPremium;
  }

  get annualPremium() {
    return this._annualPremium;
  }

  set annualPremium(annualPremium: number) {
    this._annualPremium = annualPremium;
  }

  get annualPremiumDeductions() {
    return this._annualPremiumDeductions;
  }

  set annualPremiumDeductions(annualPremiumDeductions: number) {
    this._annualPremiumDeductions = annualPremiumDeductions;
  }

  get premiumPerDeduction() {
    return this._annualPremiumDeductions === 0 ? 0 : this._annualPremium / this._annualPremiumDeductions;
  }

  // With the new cost calc value from Comm API,
  // want to show the user unrounded numbers
  // so the math makes a bit more sense
  get spendingAccountBenefit() {
    return this._spendingAccountBenefit;
  }

  get medicalCost() {
    return this._medicalCost;
  }

  get drugCost() {
    return this._drugCost;
  }

  get employerHraContribution() {
    return this._employerHraContribution === null
      ? null
      : this._employerHraContribution + this._getHRAIncentiveTotal();
  }

  get hraHasLimitedProviderReimbursement() {
    return this._hraHasLimitedProviderReimbursement;
  }

  get hraIsPostDeductibleWithHSA() {
    return this._hraIsPostDeductibleWithHSA;
  }

  get annualHraEmployerDollarsUsed() {
    return this._annualHraEmployerDollarsUsed;
  }

  get annualHsaEmployerDollarsUsed() {
    return this._annualHsaEmployerDollarsUsed;
  }

  get isHsaHraCombo() {
    return this._hsaEligible && this._hraEligible && this._hraIsPostDeductibleWithHSA === true;
  }

  get employerHsaFixedContribution() {
    return this._employerHsaFixedContribution === null
      ? null
      : this._employerHsaFixedContribution + this._getHSAIncentiveTotal();
  }

  get employerHsaPercentageMatch() {
    return this._employerHsaPercentageMatch;
  }

  get employerHsaContributionLimit() {
    return this._employerHsaContributionLimit;
  }

  get annualSihraDollarsUsed() {
    return this._annualSihraDollarsUsed;
  }

  get inNetworkDeductible() {
    return this._inNetworkDeductible;
  }

  get inNetworkMedDeductible() {
    return this._inNetworkMedDeductible;
  }

  get inNetworkDrugDeductible() {
    return this._inNetworkDrugDeductible;
  }

  get oonDeductible() {
    return this._oonDeductible;
  }

  get oonMedDeductible() {
    return this._oonMedDeductible;
  }

  get oonDrugDeductible() {
    return this._oonDrugDeductible;
  }

  get inNetworkOopMax() {
    return this._inNetworkOopMax;
  }

  get inNetworkMedOopMax() {
    return this._inNetworkMedOopMax;
  }

  get inNetworkDrugOopMax() {
    return this._inNetworkDrugOopMax;
  }

  get oonOopMax() {
    return this._oonOopMax;
  }

  get oonMedOopMax() {
    return this._oonMedOopMax;
  }

  get oonDrugOopMax() {
    return this._oonDrugOopMax;
  }

  get combinedNetworkDeductible() {
    return this._combinedNetworkDeductible;
  }

  get combinedNetworkMedDeductible() {
    return this._combinedNetworkMedDeductible;
  }

  get combinedNetworkDrugDeductible() {
    return this._combinedNetworkDrugDeductible;
  }

  // True when the plan has a Most Preferred network tier, determined by network_type_id (mirrors BC path).
  get hasMostPreferredNetwork() {
    return this._networkTypeId != null && MP_NETWORK_TYPE_IDS.has(this._networkTypeId);
  }

  // Returns deductible/OOP values from the already-tier-aware plan_details object for the given
  // network tier. No coverage-tier argument needed — comm pre-resolves the household tier.
  // Mapping follows comm's BCPlanDetails convention:
  //   most_preferred → plan_details `in_network` key
  //   in_network     → plan_details `secondary_in_network` key (or `in_network` for non-tiered plans)
  //   out_of_network → plan_details `out_of_network` key
  getPlanDetailsForNetworkTier(tier: NetworkTier) {
    if (tier === 'most_preferred') {
      return {
        medical_and_drug_deductible: this._inNetworkDeductible,
        medical_deductible: this._inNetworkMedDeductible,
        drug_deductible: this._inNetworkDrugDeductible,
        medical_and_drug_oop_max: this._inNetworkOopMax,
        medical_oop_max: this._inNetworkMedOopMax,
        drug_oop_max: this._inNetworkDrugOopMax,
      };
    }
    if (tier === 'in_network') {
      return {
        medical_and_drug_deductible:
          this._secondaryInNetworkDeductible ?? this._inNetworkDeductible ?? this._combinedNetworkDeductible,
        medical_deductible:
          this._secondaryInNetworkMedDeductible ??
          this._inNetworkMedDeductible ??
          this._combinedNetworkMedDeductible,
        drug_deductible:
          this._secondaryInNetworkDrugDeductible ??
          this._inNetworkDrugDeductible ??
          this._combinedNetworkDrugDeductible,
        medical_and_drug_oop_max:
          this._secondaryInNetworkOopMax ?? this._inNetworkOopMax ?? this._combinedNetworkOopMax,
        medical_oop_max:
          this._secondaryInNetworkMedOopMax ?? this._inNetworkMedOopMax ?? this._combinedNetworkMedOopMax,
        drug_oop_max:
          this._secondaryInNetworkDrugOopMax ?? this._inNetworkDrugOopMax ?? this._combinedNetworkDrugOopMax,
      };
    }
    return {
      medical_and_drug_deductible: this._oonDeductible,
      medical_deductible: this._oonMedDeductible,
      drug_deductible: this._oonDrugDeductible,
      medical_and_drug_oop_max: this._oonOopMax,
      medical_oop_max: this._oonMedOopMax,
      drug_oop_max: this._oonDrugOopMax,
    };
  }

  get combinedNetworkOopMax() {
    return this._combinedNetworkOopMax;
  }

  get combinedNetworkMedOopMax() {
    return this._combinedNetworkMedOopMax;
  }

  get combinedNetworkDrugOopMax() {
    return this._combinedNetworkDrugOopMax;
  }

  get individualThresholds() {
    return this._individualThresholds;
  }

  set individualThresholds(newIndividualThresholds) {
    this._individualThresholds = newIndividualThresholds;
  }

  get individualChildThresholds() {
    return this._individualChildThresholds;
  }

  get individualChildrenThresholds() {
    return this._individualChildrenThresholds;
  }

  get individualSpouseThresholds() {
    return this._individualSpouseThresholds;
  }

  get familyThresholds() {
    return this._familyThresholds;
  }

  set familyThresholds(newFamilyThresholds) {
    this._familyThresholds = newFamilyThresholds;
  }

  get primaryCare() {
    return this._primaryCare;
  }

  set primaryCare(newPrimaryCare) {
    this._primaryCare = newPrimaryCare;
  }

  get specialistCare() {
    return this._specialistCare;
  }

  set specialistCare(newSpecialistCare) {
    this._specialistCare = newSpecialistCare;
  }

  get benefitsSummaryUrl() {
    return this._benefitsSummaryUrl;
  }

  set benefitsSummaryUrl(newBenefitsSummaryUrl) {
    this._benefitsSummaryUrl = newBenefitsSummaryUrl;
  }

  get nonFormularyDrug() {
    return this._nonFormularyDrug;
  }

  set nonFormularyDrug(newNonFormularyDrug) {
    this._nonFormularyDrug = newNonFormularyDrug;
  }

  get HRAReimbursesCopay() {
    return this._canReimburseCopay;
  }

  get docfinderNetworkUuids() {
    return this._docfinderNetworkUuids;
  }

  /* Properties */

  get oopEstimate() {
    return this.medicalCost + this.drugCost;
  }

  get isEmployerMatching() {
    if (this.employerHsaPercentageMatch !== null) {
      return true;
    }

    return false;
  }

  get realCostBeforeTaxBenefit() {
    return this._realCostBeforeTaxBenefit;
  }

  get isNoDeductiblePlan() {
    const { deductible } = this._recommendation.plan_details || {};

    if (!deductible) return false;

    const carrierIsEligible = NO_DEDUCTIBLE_PLAN_CARRIERS.includes(this._carrierName);

    return carrierIsEligible && deductible.comprehensive == null && deductible.health_and_drug == null;
  }

  /* Methods */

  calcEmployerHsaContribution(hsaContributionLimit: number | null) {
    /*
      TODO: See where we can update locations that call this method
      to use annualHsaEmployerDollarsUsed instead, since these calcs
      are somewhat duplicitive with what already being done by Comm API.
      Presently, the main idfference is Comm's annualHsaEmployerDollarsUsed
      factors in incentives, whereas calcEmployerHsaMatch does not.
    */
    if (hsaContributionLimit && this.isEmployerMatching) {
      return this.calcEmployerHsaMatch(hsaContributionLimit);
    }
    return this.employerHsaFixedContribution;
  }

  calcEmployerHsaMatch(hsaContributionLimit: number | null) {
    /* Calculates employer HSA match based on user's recommended contribution amount */
    if (this.isEmployerMatching) {
      return (
        Math.min(this.oopEstimate, hsaContributionLimit || 0) -
        this.calcRecommendedHsaContribution(hsaContributionLimit)
      );
    }

    return 0;
  }

  // employerHsaPercentageMatch: Can be null
  // employerHsaContributionLimit: Can be null
  calcSlidingEmployerHsaMatch(userContribution) {
    /* Calculates the maximum amount that an employer will match based on user's contribution input */
    if (this.isEmployerMatching && this.employerHsaPercentageMatch && this.employerHsaContributionLimit) {
      return Math.min(
        (this.employerHsaPercentageMatch * userContribution) / 100,
        this.employerHsaContributionLimit,
      );
    }

    return 0;
  }

  calcRecommendedHsaContribution(hsaContributionLimit) {
    return calculateHsaRecommendation(
      this.oopEstimate,
      this.employerHsaPercentageMatch,
      this.employerHsaContributionLimit,
      hsaContributionLimit,
      this.employerHsaFixedContribution,
    );
  }

  calcHsaTaxBenefit(hsaContributionLimit) {
    /*
      TODO: Replace callers with annualHsaTaxSavings from Comm API.
      spendingAccountBenefit is BE-derived and includes any HSA annual_incentives,
      but calcEmployerHsaMatch / employerHsaFixedContribution below do not. For
      employers with HSA wellness incentives configured, this overstates the tax
      benefit by the incentive amount.
    */
    if (this.isEmployerMatching) {
      return Math.max(this.spendingAccountBenefit - this.calcEmployerHsaMatch(hsaContributionLimit), 0);
    }

    // this.employerHsaFixedContribution can be null
    const emplHsaFixedCont = this.employerHsaFixedContribution ?? 0;
    return Math.max(this.spendingAccountBenefit - emplHsaFixedCont, 0);
  }

  calcOutOfPocket() {
    return Math.floor((this.realCostBeforeTaxBenefit - this.annualPremium) * 100) / 100;
  }

  getInNetworkThreshold(coverageTier) {
    switch (coverageTier) {
      case COVERAGE_TIERS.INDIVIDUAL: {
        return this.individualThresholds.in_network;
      }
      case COVERAGE_TIERS.INDIVIDUAL_CHILD: {
        return this.individualChildThresholds.in_network;
      }
      case COVERAGE_TIERS.INDIVIDUAL_CHILDREN: {
        return this.individualChildrenThresholds.in_network;
      }
      case COVERAGE_TIERS.INDIVIDUAL_SPOUSE: {
        return this.individualSpouseThresholds.in_network;
      }
      default: {
        return this.familyThresholds.in_network;
      }
    }
  }

  getInNetworkDeductible(coverageTier) {
    const thresholds = this.getInNetworkThreshold(coverageTier);
    return thresholds.medical_and_drug_deductible || thresholds.medical_deductible || 0;
  }

  getOutOfNetworkThreshold(coverageTier) {
    switch (coverageTier) {
      case COVERAGE_TIERS.INDIVIDUAL: {
        return this.individualThresholds.out_of_network;
      }
      case COVERAGE_TIERS.INDIVIDUAL_CHILD: {
        return this.individualChildThresholds.out_of_network;
      }
      case COVERAGE_TIERS.INDIVIDUAL_CHILDREN: {
        return this.individualChildrenThresholds.out_of_network;
      }
      case COVERAGE_TIERS.INDIVIDUAL_SPOUSE: {
        return this.individualSpouseThresholds.out_of_network;
      }
      default: {
        return this.familyThresholds.out_of_network;
      }
    }
  }

  getThresholdsByCoverageTier(coverageTier: COVERAGE_TIERS) {
    switch (coverageTier) {
      case COVERAGE_TIERS.INDIVIDUAL:
        return this.individualThresholds;
      case COVERAGE_TIERS.INDIVIDUAL_CHILD:
        return this.individualChildThresholds ?? this.familyThresholds ?? this.individualThresholds;
      case COVERAGE_TIERS.INDIVIDUAL_CHILDREN:
        return this.individualChildrenThresholds ?? this.familyThresholds ?? this.individualThresholds;
      case COVERAGE_TIERS.INDIVIDUAL_SPOUSE:
        return this.individualSpouseThresholds ?? this.familyThresholds ?? this.individualThresholds;
      default:
        return this.familyThresholds ?? this.individualThresholds;
    }
  }

  // TODO - Remove when /forecasts API supports new coverage tiers
  getIsInNetworkOopLimitComprehensiveLegacy(coverageTier) {
    const threshold = this.getInNetworkThreshold(coverageTier);

    return (
      threshold.medical_oop_max == null &&
      threshold.drug_oop_max == null &&
      threshold.medical_and_drug_oop_max !== null
    );
  }

  getIsInNetworkOopLimitComprehensive() {
    return this.inNetworkDrugOopMax == null && this.inNetworkMedOopMax == null;
  }

  // TODO - Remove when /forecasts API supports new coverage tiers
  getIsInNetworkDeductibleComprehensiveLegacy(coverageTier) {
    const threshold = this.getInNetworkThreshold(coverageTier);

    return (
      threshold.medical_deductible == null &&
      threshold.drug_deductible == null &&
      threshold.medical_and_drug_deductible !== null
    );
  }

  getIsInNetworkDeductibleComprehensive() {
    return this.inNetworkDrugDeductible == null && this.inNetworkMedDeductible == null;
  }

  getIsOonOopLimitComprehensive() {
    return this.oonDrugOopMax == null && this.oonMedOopMax == null;
  }

  getIsOonDeductibleComprehensive() {
    return this.oonDrugDeductible == null && this.oonMedDeductible == null;
  }

  hasInNetworkDeductible() {
    return this.inNetworkMedDeductible != null || this.inNetworkDeductible != null;
  }

  hasCombinedNetworkDeductible() {
    return this.combinedNetworkMedDeductible != null || this.combinedNetworkDeductible != null;
  }

  hasInNetworkOopMax() {
    return this.inNetworkMedOopMax != null || this.inNetworkOopMax != null;
  }

  hasCombinedNetworkOopMax() {
    return this.combinedNetworkMedOopMax != null || this.combinedNetworkOopMax != null;
  }

  getIsCombinedNetworkDeductibleComprehensive() {
    return this.combinedNetworkDrugDeductible == null && this.combinedNetworkMedDeductible == null;
  }

  getIsCombinedNetworkOopLimitComprehensive() {
    return this.combinedNetworkDrugOopMax == null && this.combinedNetworkMedOopMax == null;
  }

  _getHSAIncentiveTotal() {
    const incentives = this._recommendation.plan_details.health_savings_account?.annual_incentives;
    return incentives?.reduce((acc, { amount }) => acc + amount, 0) || 0;
  }

  _getHRAIncentiveTotal() {
    const incentives = this._recommendation.plan_details.health_reimbursement_arrangement?.annual_incentives;
    return incentives?.reduce((acc, { amount }) => acc + amount, 0) || 0;
  }
}

export default Plan;
